import { XIcon } from '@heroicons/react/outline';
import { Modal, QRCode } from 'antd';
import AppStoreGetIcon from 'assets/Icons/AppStoreGetIcon';
import CheckSuccessIcon from 'assets/Icons/CheckSuccessIcon';
import PlayStoreGetIcon from 'assets/Icons/PlayStoreGetIcon';
import mixpanel from 'mixpanel-browser';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';

import { user } from '@/atoms/user';
import { User } from '@/utils/interfaces/User';

interface SuccessModalProps {
  isVisible: boolean;
  onClose: () => void;
  onGoToLibrary: () => void;
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  isVisible,
  onClose,
  onGoToLibrary,
}) => {
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const environment = process.env.NEXT_PUBLIC_APP_ENV;

  const [androidRedirectUrl, setAndroidRedirectUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);

    const defaultParams: Record<string, string> = {
      utm_source: 'zebralearn',
      utm_medium: 'website',
      utm_campaign: 'download_app',
    };

    const finalParams = new URLSearchParams();

    urlParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!finalParams.has(key)) {
        finalParams.set(key, value);
      }
    });

    const encodedReferrer = encodeURIComponent(finalParams.toString());
    const androidUrl = `https://play.google.com/store/apps/details?id=com.zebralearn.reader&referrer=${encodedReferrer}`;

    setAndroidRedirectUrl(androidUrl);
  }, []);

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={500}
      closeIcon={<XIcon className='text-white w-5 h-5' />}
      className='success-modal'
      styles={{
        content: {
          backgroundColor: '#121212',
          borderRadius: '16px',
          padding: '24px',
          paddingBottom: '40px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <div className='flex flex-col items-center text-center'>
        <div className='w-16 h-16 rounded-full flex items-center justify-center'>
          <CheckSuccessIcon />
        </div>

        <div className='py-6'>
          <h2 className='text-white text-xl font-semibold mb-2'>
            Digital Book Granted
          </h2>
          <p className='text-grayZl-500 text-base font-light'>
            Kindly check your Library for access
          </p>
        </div>

        <ButtonDefault
          size={4}
          variant={ButtonVariants.PRIMARY}
          rounded={false}
          roundedSm={true}
          onClick={onGoToLibrary}
          className=' text-white mb-6'
        >
          <span className='px-6 text-sm font-semibold'>Go To Library</span>
        </ButtonDefault>

        <div className='flex items-center w-full  mb-4'>
          <hr className='flex-grow border-t border-grayZl-850' />
          <span className='mx-2 text-white text-sm'>Or</span>
          <hr className='flex-grow border-t border-grayZl-850' />
        </div>

        <div className='mb-3 '>
          <QRCode
            errorLevel='H'
            value='https://zebralearn.com/get-our-app/'
            icon='https://zebralearn.com/logo.svg'
            className='rounded-xl'
          />
        </div>

        <p className='text-sm mb-3'>Download Our App</p>

        <div className='flex gap-x-1'>
          <ButtonDefault
            size={0}
            rounded={false}
            roundedSm={true}
            onClick={() => {
              const eventParams: EventDetails = {
                event_name: 'play_store',
                source: 'global',
                source_type: 'app_download',
                source_id: null,
                sub_source: null,
                sub_source_id: null,
                unit: 'boolean',
                value: 1,
              };

              logEvent({
                eventDetails: eventParams,
                userData: userData,
                pathname: router.pathname,
              });
              if (
                typeof mixpanel !== 'undefined' &&
                environment === 'production'
              ) {
                mixpanel.track(eventParams.event_name as string, eventParams);
              }
              router.push(androidRedirectUrl as string);
            }}
            className='border-none bg-transparent p-0.5'
            variant={ButtonVariants.UNSTYLED}
          >
            <PlayStoreGetIcon />
          </ButtonDefault>
          <ButtonDefault
            size={0}
            rounded={false}
            roundedSm={true}
            onClick={() => {
              const eventParams: EventDetails = {
                event_name: 'app_store',
                source: 'global',
                source_type: 'app_download',
                source_id: null,
                sub_source: null,
                sub_source_id: null,
                unit: 'boolean',
                value: 1,
              };

              logEvent({
                eventDetails: eventParams,
                userData: userData,
                pathname: router.pathname,
              });
              if (
                typeof mixpanel !== 'undefined' &&
                environment === 'production'
              ) {
                mixpanel.track(eventParams.event_name as string, eventParams);
              }
              router.push(
                'https://apps.apple.com/in/app/zebralearn/id6477746268?utm_source=zebralearn_website',
              );
            }}
            className='border-none bg-transparent p-0.5 '
            variant={ButtonVariants.UNSTYLED}
          >
            <AppStoreGetIcon />
          </ButtonDefault>
        </div>
      </div>
    </Modal>
  );
};

export default SuccessModal;
